/**
 * Canonical brand data for TRBD.
 *
 * Single source of truth for:
 *   - Founder profile
 *   - Social channels (personal + company Telegram bot)
 *   - WhatsApp numbers
 *   - App-store developer links
 *   - Headline stats
 *   - Flagship products
 *
 * Every entry carries everything the UI needs to render itself (urls, labels,
 * brand colors), so call-sites stop hardcoding hex values and copy-pasting URLs.
 */

export const FOUNDER = {
    nameAr: 'المهندس أحمد علولو',
    nameEn: 'Eng. Ahmed Allulu',
    titleEn: 'CEO & Founder',
    titleAr: 'المؤسس والرئيس التنفيذي',
    avatar: '/assets/robot-avatar.png',
    bioAr: 'مؤسس TRBD و CarWhats Group. 7+ سنوات خبرة في بناء منصات ومنتجات رقمية تخدم عملاء في أكثر من دولة حول العالم.',
    bioEn: 'Founder of TRBD & CarWhats Group. 7+ years building digital platforms and products serving clients worldwide.',
} as const;

export const SOCIALS = {
    linkedin: {
        url: 'https://www.linkedin.com/in/ahmed-allulu-96605b404/',
        labelAr: 'لينكد إن',
        labelEn: 'LinkedIn',
        brand: '#0a66c2',
    },
    youtube: {
        url: 'https://www.youtube.com/channel/UC2h0v_PBUQzODLNT9EIUGzw',
        labelAr: 'يوتيوب',
        labelEn: 'YouTube',
        brand: '#FF0000',
    },
    facebook: {
        url: 'https://www.facebook.com/AhmedAllulu.tech',
        labelAr: 'فيسبوك',
        labelEn: 'Facebook',
        brand: '#1877F2',
    },
    telegram: {
        url: 'https://t.me/trbd_sybot',
        labelAr: 'تيليجرام',
        labelEn: 'Telegram',
        brand: '#229ED9',
    },
} as const;

export type SocialKey = keyof typeof SOCIALS;

export const WHATSAPP = {
    tr: {
        url: 'https://wa.me/905537323153',
        display: '+90 553 732 3153',
        labelAr: 'واتساب تركيا',
    },
    sy: {
        url: 'https://wa.me/963992367582',
        display: '+963 992 367 582',
        labelAr: 'واتساب سوريا',
    },
} as const;

export const APP_STORES = {
    googlePlay: {
        url: 'https://play.google.com/store/apps/dev?id=5700825962896136120',
        labelEn: 'Google Play',
        captionEn: 'GET IT ON',
    },
    appStore: {
        url: 'https://apps.apple.com/us/developer/ahmed-allulu/id1762488678',
        labelEn: 'App Store',
        captionEn: 'Download on the',
    },
} as const;

export const STATS = {
    projects: '45+',
    clients: '102+',
    yearsExperience: '7+',
    satisfaction: '99%',
} as const;

/**
 * Flagship products. Fields cover both the compact home-page strip
 * (name/nameAr/url/descAr/gradient) and the detailed portfolio page
 * (adds descArLong/gradientRich/accent/tags). A single array, two read patterns.
 */
export const FLAGSHIPS = [
    {
        key: 'kasioon',
        name: 'Kasioon',
        nameAr: 'قاسيون',
        url: 'https://kasioon.com',
        descAr: 'منصة متكاملة + تطبيقات iOS/Android',
        descArLong: 'منصة متكاملة مع تطبيقات iOS و Android. تجربة مستخدم سلسة وتصميم عصري يخدم شريحة واسعة من المستخدمين.',
        gradient: 'from-purple-500/20 to-pink-500/10',
        gradientRich: 'from-purple-500/20 via-fuchsia-500/10 to-pink-500/5',
        accent: 'bg-purple-500',
        tags: ['React', 'Node.js', 'iOS', 'Android'],
    },
    {
        key: 'carwhats-group',
        name: 'CarWhats Group',
        nameAr: 'مجموعة كارواتس',
        url: 'https://carwhats-group.com',
        descAr: 'البوابة الرئيسية لمجموعة السيارات',
        descArLong: 'البوابة الرئيسية لمجموعة CarWhats. بوابة شركات بتصميم احترافي تربط بين الخدمات والمنتجات تحت مظلة واحدة.',
        gradient: 'from-blue-500/20 to-cyan-500/10',
        gradientRich: 'from-blue-500/20 via-cyan-500/10 to-sky-500/5',
        accent: 'bg-blue-500',
        tags: ['Next.js', 'TypeScript', 'PostgreSQL'],
    },
    {
        key: 'carwhats',
        name: 'CarWhats',
        nameAr: 'كارواتس',
        url: 'https://carwhats.com',
        descAr: 'تطبيق + موقع للمستهلكين',
        descArLong: 'منصة + تطبيقات للمستهلكين. تسهّل اختيار السيارات وتواصل المستخدمين مع الوكلاء والخدمات المرتبطة.',
        gradient: 'from-emerald-500/20 to-teal-500/10',
        gradientRich: 'from-emerald-500/20 via-teal-500/10 to-green-500/5',
        accent: 'bg-emerald-500',
        tags: ['Flutter', 'Firebase', 'REST API'],
    },
] as const;

export type Flagship = (typeof FLAGSHIPS)[number];
